import BuilderActions from "./builderActions";

const upsertMessage = (messages, message) => {
  if (!message) return messages;
  const existingIndex = messages.findIndex((item) => item.uid === message.uid);
  if (existingIndex >= 0) {
    return messages.map((item, index) => (index === existingIndex ? message : item));
  }
  return [...messages, message];
};

const updatePlanApprovalState = (message, state) => {
  if (!message) return message;
  let metadata = {};
  if (message.metadataJson) {
    try {
      metadata = JSON.parse(message.metadataJson);
    } catch (err) {
      metadata = {};
    }
  }
  metadata.planApproval = {
    ...(metadata.planApproval || {}),
    state,
    updatedAt: new Date().toISOString(),
  };
  return {
    ...message,
    planApprovalNeeded: state === "pending",
    metadataJson: JSON.stringify(metadata),
  };
};

const initialState = {
  // Project list state
  projectList: null,
  listCounter: 0,
  deletingProjectUid: null,  // UID of project currently being deleted

  // Current project (single project view on chat page)
  currentProject: null,
  currentProjectLoading: false,

  // Chat state
  chat: null,
  messages: [],
  replyInProgress: false,
  replyError: null,
  approvingPlan: false,
  planningProgress: null,  // Current planning phase thinking text (shown in loading indicator)

  // Agent execution state
  agentSession: null,        // { sessionId, status: 'RUNNING'|'COMPLETED'|'FAILED'|'STOPPED', isActive }
  agentTodos: [],            // Array of { content, activeForm, status }
  agentCurrentTask: null,    // Current task being executed
  agentFileChanges: [],      // Array of { path, action, timestamp }
  agentToolCalls: [],        // Array of { tool, input, output, timestamp }
  agentTokenUsage: {         // Token usage tracking
    inputTokens: 0,
    outputTokens: 0,
    totalCost: 0,
  },
  agentError: null,          // Error message if failed

  // Connection resilience state
  lastEventId: null,         // Last received event ID for resume (persists across reconnects)
  shouldResumeAgent: false,  // True only when we need to resume on reconnect (page reload, not fresh session)

  // Per-project state - keyed by projectUid to prevent cross-project leakage
  currentProjectUid: null,        // Currently active project
  sourcesByProject: {},           // { [projectUid]: { tree, openTabs, activeTabPath, expandedPaths, searchQuery, searchResults, externalChange } }
  sourcesTreeLoading: false,      // Global loading indicators (transient UI state)
  sourcesSearchLoading: false,

  // Project events - also per-project
  projectEventsByProject: {},     // { [projectUid]: events[] }
  projectEventsLoading: false,

  // Template state
  templates: null,
  templatesLoading: false,
  templatesError: null,
  currentTemplate: null,
  currentTemplateLoading: false,
  templateExporting: false,
  templateInstantiating: false,
  deletingTemplateUid: null,
};

// Helper to get project-specific sources state with defaults
const getProjectSourcesState = (state, projectUid) => {
  return state.sourcesByProject[projectUid] || {
    tree: null,
    openTabs: [],
    activeTabPath: null,
    expandedPaths: [],
    searchQuery: "",
    searchResults: null,
    externalChange: null,
    // File change notification state
    pendingFileChanges: [],      // Queue of { path, action, timestamp }
    needsTreeRefresh: false,     // Flag to refresh tree after debounce
    isReloading: false,          // Batch reload in progress
  };
};

// Helper to update project-specific sources state
const setProjectSourcesState = (state, projectUid, updates) => {
  if (!projectUid) return state;  // Safety check
  const currentState = getProjectSourcesState(state, projectUid);
  return {
    ...state,
    sourcesByProject: {
      ...state.sourcesByProject,
      [projectUid]: {
        ...currentState,
        ...updates,
      },
    },
  };
};

const builderReducer = (state = initialState, action) => {
  switch (action.type) {
    // Project list actions
    case BuilderActions.LOADING_PROJECTS:
      return {
        ...state,
        projectList: null,
      };
    case BuilderActions.LOADED_PROJECTS:
      return {
        ...state,
        projectList: action.projects,
      };
    case BuilderActions.PROJECT_CREATED:
      return {
        ...state,
        listCounter: state.listCounter + 1,
      };
    case BuilderActions.PROJECT_UPDATED: {
      const updatedProject = action.payload.project;
      console.log("[builderReducer] PROJECT_UPDATED:", {
        uid: updatedProject?.uid,
        subdomain: updatedProject?.subdomain,
        projectListLength: state.projectList?.length,
      });
      return {
        ...state,
        projectList: state.projectList
          ? state.projectList.map((p) =>
              p.uid === updatedProject.uid ? updatedProject : p
            )
          : null,
      };
    }
    case BuilderActions.DELETING_PROJECT:
      return {
        ...state,
        deletingProjectUid: action.uid,
      };
    case BuilderActions.PROJECT_DELETED:
      return {
        ...state,
        listCounter: state.listCounter + 1,
        deletingProjectUid: null,
      };

    // Current project actions (single project view)
    case BuilderActions.CURRENT_PROJECT_LOADING:
      return {
        ...state,
        currentProjectLoading: true,
      };
    case BuilderActions.CURRENT_PROJECT_LOADED:
      return {
        ...state,
        currentProject: action.payload.project,
        currentProjectLoading: false,
      };
    case BuilderActions.CURRENT_PROJECT_UPDATED:
      return {
        ...state,
        currentProject: action.payload.project,
      };
    case BuilderActions.CURRENT_PROJECT_CLEARED:
      return {
        ...state,
        currentProject: null,
        currentProjectLoading: false,
      };

    // Chat actions
    case BuilderActions.LOADING_CHAT:
      return {
        ...state,
        chat: null,
        messages: [],
        replyInProgress: false,
        replyError: null,
      };
    case BuilderActions.LOADED_CHAT: {
      const tempMessages = state.messages.filter((message) =>
        String(message.uid || "").startsWith("temp-")
      );
      let mergedMessages = action.messages || [];
      tempMessages.forEach((message) => {
        mergedMessages = upsertMessage(mergedMessages, message);
      });
      return {
        ...state,
        chat: action.chat,
        messages: mergedMessages,
        planningProgress: null,
      };
    }
    case BuilderActions.POSTED_MESSAGE: {
      const keepReplyInProgress = action.skipReplyInProgress === true;
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            uid: "temp-" + Date.now(),
            role: "user",
            content: action.content,
            dateCreated: new Date().toISOString(),
          },
        ],
        replyInProgress: keepReplyInProgress ? state.replyInProgress : true,
        replyError: null,
      };
    }
    case BuilderActions.GOT_MESSAGE_RESPONSE: {
      let nextMessages = upsertMessage(state.messages, action.message);
      if (action.message?.planMd && action.message?.planApprovalNeeded === true) {
        const previousPendingIndex = nextMessages.findIndex(
          (message) => message.planApprovalNeeded === true && message.uid !== action.message.uid
        );
        if (previousPendingIndex >= 0) {
          const updated = updatePlanApprovalState(nextMessages[previousPendingIndex], "superseded");
          nextMessages = nextMessages.map((message, index) =>
            index === previousPendingIndex ? updated : message
          );
        }
      }
      const shouldClearPlanning = Boolean(action.message?.planMd || action.message?.planApprovalNeeded);
      return {
        ...state,
        messages: nextMessages,
        replyInProgress: false,
        planningProgress: shouldClearPlanning ? null : state.planningProgress,
        approvingPlan: false,
      };
    }
    case BuilderActions.UPDATE_CHAT_MESSAGE: {
      return {
        ...state,
        messages: upsertMessage(state.messages, action.message),
      };
    }
    case BuilderActions.APPROVING_PLAN:
      return {
        ...state,
        approvingPlan: true,
        replyError: null,
      };
    case BuilderActions.MESSAGE_ERROR:
      return {
        ...state,
        replyInProgress: false,
        approvingPlan: false,
        replyError: action.error,
      };
    case BuilderActions.CLEAR_CHAT:
      return {
        ...state,
        chat: null,
        messages: [],
        replyInProgress: false,
        replyError: null,
        approvingPlan: false,
        planningProgress: null,
      };

    // Agent execution actions
    case BuilderActions.AGENT_SESSION_INIT: {
      // Fresh session just started - don't need to resume (events come via streaming)
      return {
        ...state,
        agentSession: { sessionId: action.sessionId, status: action.status, isActive: true },
        agentTodos: [],
        agentFileChanges: [],
        agentToolCalls: [],
        agentTokenUsage: { inputTokens: 0, outputTokens: 0, totalCost: 0 },
        agentError: null,
        approvingPlan: false,
        shouldResumeAgent: false,  // Fresh session - events stream live, no need to resume
      };
    }

    case BuilderActions.AGENT_SESSION_LOADED: {
      // Restore agent state from backend on page load (page refresh or reconnect)
      const session = action.session;
      const todos = session.todosJson ? JSON.parse(session.todosJson) : [];
      return {
        ...state,
        agentSession: {
          sessionId: session.sessionUid,
          status: session.status,
          isActive: session.isActive,
        },
        agentTodos: todos,
        agentCurrentTask: todos.find((t) => t.status === "in_progress")?.content || null,
        agentTokenUsage: {
          inputTokens: session.inputTokensUsed || 0,
          outputTokens: session.outputTokensUsed || 0,
          totalCost: session.costUsd || 0,
        },
        // Only resume if session is active - this means we're reconnecting to an existing session
        shouldResumeAgent: session.isActive === true,
      };
    }

    case BuilderActions.AGENT_TODO_UPDATE:
      return {
        ...state,
        agentTodos: action.todos,
        agentCurrentTask: action.currentTask,
      };

    case BuilderActions.AGENT_FILE_CHANGE:
      return {
        ...state,
        agentFileChanges: [...state.agentFileChanges, action.change],
      };

    case BuilderActions.AGENT_TOOL_CALL:
      return {
        ...state,
        agentToolCalls: [...state.agentToolCalls, action.toolCall],
      };

    case BuilderActions.AGENT_TOKEN_USAGE:
      return {
        ...state,
        agentTokenUsage: {
          inputTokens: action.inputTokens,
          outputTokens: action.outputTokens,
          totalCost: action.totalCost,
        },
      };

    case BuilderActions.AGENT_MESSAGE:
      return state;

    case BuilderActions.AGENT_PLANNING_PROGRESS:
      // Update planning progress (shown in loading indicator, not as chat message)
      return {
        ...state,
        planningProgress: action.content,
      };

    case BuilderActions.AGENT_COMPLETE: {
      return {
        ...state,
        agentSession: {
          ...state.agentSession,
          status: action.success ? "COMPLETED" : "FAILED",
          isActive: false,
        },
        agentTokenUsage: {
          ...state.agentTokenUsage,
          totalCost: action.totalCost || state.agentTokenUsage.totalCost,
        },
        replyInProgress: false,
        approvingPlan: false,       // Clear approving state when agent completes
      };
    }

    case BuilderActions.AGENT_ERROR: {
      const isRecoverable = action.recoverable;
      return {
        ...state,
        agentSession: {
          ...state.agentSession,
          status: isRecoverable ? state.agentSession?.status : "FAILED",
          isActive: isRecoverable ? state.agentSession?.isActive : false,
        },
        agentError: action.message,
        replyInProgress: isRecoverable ? state.replyInProgress : false,
        approvingPlan: isRecoverable ? state.approvingPlan : false,
      };
    }

    case BuilderActions.AGENT_CLEAR:
      return {
        ...state,
        agentSession: null,
        agentTodos: [],
        agentCurrentTask: null,
        agentFileChanges: [],
        agentToolCalls: [],
        agentTokenUsage: { inputTokens: 0, outputTokens: 0, totalCost: 0 },
        agentError: null,
        shouldResumeAgent: false,
        planningProgress: null,
      };

    // Connection resilience actions
    case BuilderActions.LAST_EVENT_ID_UPDATED:
      return {
        ...state,
        lastEventId: action.eventId,
      };

    // Project context
    case BuilderActions.SET_CURRENT_PROJECT:
      return {
        ...state,
        currentProjectUid: action.projectUid,
      };

    // Sources (file tree/editor) actions
    case BuilderActions.SOURCES_TREE_LOADING:
      return {
        ...state,
        sourcesTreeLoading: true,
      };

    case BuilderActions.SOURCES_TREE_LOADED:
      return {
        ...setProjectSourcesState(state, action.projectUid, {
          tree: action.tree,
        }),
        sourcesTreeLoading: false,
      };

    case BuilderActions.SOURCES_FOLDER_EXPANDED: {
      // Merge children into the tree at the specified path
      const projectState = getProjectSourcesState(state, action.projectUid);
      const updateTreeNode = (nodes, targetPath, children) => {
        return nodes.map((node) => {
          if (node.path === targetPath) {
            return { ...node, children, hasChildren: children.length > 0 };
          }
          if (node.children && targetPath.startsWith(node.path + "/")) {
            return { ...node, children: updateTreeNode(node.children, targetPath, children) };
          }
          return node;
        });
      };
      return setProjectSourcesState(state, action.projectUid, {
        tree: updateTreeNode(projectState.tree || [], action.path, action.children),
      });
    }

    case BuilderActions.SOURCES_SEARCH_LOADING:
      return {
        ...setProjectSourcesState(state, action.projectUid, {
          searchQuery: action.query,
        }),
        sourcesSearchLoading: true,
      };

    case BuilderActions.SOURCES_SEARCH_LOADED:
      return {
        ...setProjectSourcesState(state, action.projectUid, {
          searchResults: action.results,
        }),
        sourcesSearchLoading: false,
      };

    case BuilderActions.SOURCES_SEARCH_CLEAR:
      return {
        ...setProjectSourcesState(state, action.projectUid, {
          searchQuery: "",
          searchResults: null,
        }),
        sourcesSearchLoading: false,
      };

    case BuilderActions.SOURCES_FILE_OPENED: {
      // Add file to open tabs if not already open
      const projectStateOpen = getProjectSourcesState(state, action.projectUid);
      const existingTab = projectStateOpen.openTabs.find((tab) => tab.path === action.file.path);
      if (existingTab) {
        return setProjectSourcesState(state, action.projectUid, {
          activeTabPath: action.file.path,
        });
      }
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: [
          ...projectStateOpen.openTabs,
          {
            path: action.file.path,
            name: action.file.name,
            content: null,  // Content loaded separately
            isDirty: false,
            isSaving: false,
            lastSavedContent: null,
            readOnly: action.file.readOnly || false,
            mimeType: action.file.mimeType || "text/plain",
          },
        ],
        activeTabPath: action.file.path,
      });
    }

    case BuilderActions.SOURCES_FILE_CLOSED: {
      const projectStateClose = getProjectSourcesState(state, action.projectUid);
      const newTabs = projectStateClose.openTabs.filter((tab) => tab.path !== action.path);
      let newActiveTab = projectStateClose.activeTabPath;
      if (projectStateClose.activeTabPath === action.path) {
        // Select the previous tab, or the next one, or null
        const closedIndex = projectStateClose.openTabs.findIndex((tab) => tab.path === action.path);
        if (newTabs.length > 0) {
          newActiveTab = newTabs[Math.min(closedIndex, newTabs.length - 1)].path;
        } else {
          newActiveTab = null;
        }
      }
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: newTabs,
        activeTabPath: newActiveTab,
      });
    }

    case BuilderActions.SOURCES_TAB_ACTIVATED:
      return setProjectSourcesState(state, action.projectUid, {
        activeTabPath: action.path,
      });

    case BuilderActions.SOURCES_FILE_CONTENT_LOADED: {
      const projectStateContent = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateContent.openTabs.map((tab) =>
          tab.path === action.path
            ? { ...tab, content: action.content, lastSavedContent: action.content, readOnly: action.readOnly }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_CONTENT_LOAD_ERROR: {
      const projectStateError = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateError.openTabs.map((tab) =>
          tab.path === action.path
            ? { ...tab, content: "", loadError: action.error }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_CONTENT_CHANGED: {
      const projectStateChanged = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateChanged.openTabs.map((tab) =>
          tab.path === action.path
            ? { ...tab, content: action.content, isDirty: tab.lastSavedContent !== action.content }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_SAVING: {
      const projectStateSaving = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateSaving.openTabs.map((tab) =>
          tab.path === action.path ? { ...tab, isSaving: true } : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_SAVED: {
      const projectStateSaved = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateSaved.openTabs.map((tab) =>
          tab.path === action.path
            ? { ...tab, isSaving: false, isDirty: false, lastSavedContent: tab.content }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_SAVE_ERROR: {
      const projectStateSaveError = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateSaveError.openTabs.map((tab) =>
          tab.path === action.path ? { ...tab, isSaving: false } : tab
        ),
      });
    }

    case BuilderActions.SOURCES_FILE_EXTERNAL_CHANGE:
      return setProjectSourcesState(state, action.projectUid, {
        externalChange: { path: action.path, action: action.changeAction },
      });

    case BuilderActions.SOURCES_MARK_FILES_STALE: {
      // Mark specific files as stale (content may be outdated)
      const projectStateStale = getProjectSourcesState(state, action.projectUid);
      const stalePaths = new Set(action.paths);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateStale.openTabs.map((tab) =>
          stalePaths.has(tab.path)
            ? { ...tab, isStale: true, staleTimestamp: Date.now() }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_QUEUE_FILE_CHANGE: {
      // Queue a file change for debounced processing
      const projectStateQueue = getProjectSourcesState(state, action.projectUid);
      const existingIndex = projectStateQueue.pendingFileChanges.findIndex(
        (c) => c.path === action.change.path
      );
      let newPendingChanges;
      if (existingIndex >= 0) {
        // Update existing change (newer action takes precedence)
        newPendingChanges = projectStateQueue.pendingFileChanges.map((c, i) =>
          i === existingIndex ? action.change : c
        );
      } else {
        newPendingChanges = [...projectStateQueue.pendingFileChanges, action.change];
      }
      return setProjectSourcesState(state, action.projectUid, {
        pendingFileChanges: newPendingChanges,
      });
    }

    case BuilderActions.SOURCES_SET_DEBOUNCE_TIMER:
      // Timer ID stored externally (in action creator), just acknowledge
      return state;

    case BuilderActions.SOURCES_FLAG_TREE_REFRESH:
      return setProjectSourcesState(state, action.projectUid, {
        needsTreeRefresh: true,
      });

    case BuilderActions.SOURCES_BATCH_RELOAD_START:
      return setProjectSourcesState(state, action.projectUid, {
        isReloading: true,
      });

    case BuilderActions.SOURCES_BATCH_RELOAD_COMPLETE: {
      // Update tabs with reloaded content
      const projectStateReload = getProjectSourcesState(state, action.projectUid);
      const resultsByPath = {};
      action.results.forEach((r) => {
        resultsByPath[r.path] = r;
      });
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateReload.openTabs.map((tab) => {
          const result = resultsByPath[tab.path];
          if (!result) return tab;
          if (result.success) {
            // Only update if tab is not dirty, otherwise keep stale flag for conflict modal
            if (!tab.isDirty) {
              return {
                ...tab,
                content: result.content,
                lastSavedContent: result.content,
                isStale: false,
                staleTimestamp: null,
              };
            }
            // Tab is dirty - keep content but mark for conflict resolution
            return { ...tab, pendingContent: result.content };
          }
          // Load failed - clear stale flag, keep existing content
          return { ...tab, isStale: false, staleTimestamp: null, loadError: result.error };
        }),
        isReloading: false,
      });
    }

    case BuilderActions.SOURCES_CLEAR_PENDING_CHANGES:
      return setProjectSourcesState(state, action.projectUid, {
        pendingFileChanges: [],
        needsTreeRefresh: false,
      });

    case BuilderActions.SOURCES_CLEAR_STALE_FLAG: {
      // Clear stale flag for a specific file (after user resolves conflict)
      const projectStateClear = getProjectSourcesState(state, action.projectUid);
      return setProjectSourcesState(state, action.projectUid, {
        openTabs: projectStateClear.openTabs.map((tab) =>
          tab.path === action.path
            ? { ...tab, isStale: false, staleTimestamp: null, pendingContent: null }
            : tab
        ),
      });
    }

    case BuilderActions.SOURCES_PATH_TOGGLED: {
      const projectStateToggle = getProjectSourcesState(state, action.projectUid);
      const path = action.payload;
      const currentPaths = projectStateToggle.expandedPaths;
      const isExpanded = currentPaths.includes(path);
      return setProjectSourcesState(state, action.projectUid, {
        expandedPaths: isExpanded
          ? currentPaths.filter((p) => p !== path)
          : [...currentPaths, path],
      });
    }

    case BuilderActions.SOURCES_PATHS_EXPANDED: {
      const projectStateExpand = getProjectSourcesState(state, action.projectUid);
      const paths = action.payload;
      const currentPathsSet = new Set(projectStateExpand.expandedPaths);
      paths.forEach((p) => currentPathsSet.add(p));
      return setProjectSourcesState(state, action.projectUid, {
        expandedPaths: Array.from(currentPathsSet),
      });
    }

    case BuilderActions.SOURCES_RESET:
      // Only clear search state for current project - keep tabs and expanded paths
      return setProjectSourcesState(state, action.projectUid, {
        searchQuery: "",
        searchResults: null,
      });

    case BuilderActions.SOURCES_CLEAR_PROJECT: {
      // Remove all state for a specific project
      const newSourcesByProject = { ...state.sourcesByProject };
      delete newSourcesByProject[action.projectUid];
      const newEventsByProject = { ...state.projectEventsByProject };
      delete newEventsByProject[action.projectUid];
      return {
        ...state,
        sourcesByProject: newSourcesByProject,
        projectEventsByProject: newEventsByProject,
      };
    }

    // Project events actions
    case BuilderActions.PROJECT_EVENTS_LOADING:
      return {
        ...state,
        projectEventsLoading: true,
      };

    case BuilderActions.PROJECT_EVENTS_LOADED: {
      return {
        ...state,
        projectEventsByProject: {
          ...state.projectEventsByProject,
          [action.projectUid]: action.events || [],
        },
        projectEventsLoading: false,
      };
    }

    case BuilderActions.PROJECT_EVENT_RECEIVED: {
      // Update or add the event for the specific project
      const currentEvents = state.projectEventsByProject[action.projectUid] || [];
      const existingIndex = currentEvents.findIndex((e) => e.id === action.event.id);
      let newEvents;
      if (existingIndex >= 0) {
        newEvents = currentEvents.map((e, i) =>
          i === existingIndex ? action.event : e
        );
      } else {
        newEvents = [action.event, ...currentEvents].slice(0, 50); // Keep max 50
      }
      return {
        ...state,
        projectEventsByProject: {
          ...state.projectEventsByProject,
          [action.projectUid]: newEvents,
        },
      };
    }

    // Template actions
    case BuilderActions.TEMPLATES_LOADING:
      return {
        ...state,
        templatesLoading: true,
        templatesError: null,
      };

    case BuilderActions.TEMPLATES_LOADED:
      return {
        ...state,
        templates: action.templates,
        templatesLoading: false,
      };

    case BuilderActions.TEMPLATES_LOAD_ERROR:
      return {
        ...state,
        templatesLoading: false,
        templatesError: action.error,
      };

    case BuilderActions.TEMPLATE_LOADING:
      return {
        ...state,
        currentTemplateLoading: true,
      };

    case BuilderActions.TEMPLATE_LOADED:
      return {
        ...state,
        currentTemplate: action.template,
        currentTemplateLoading: false,
      };

    case BuilderActions.TEMPLATE_LOAD_ERROR:
      return {
        ...state,
        currentTemplateLoading: false,
      };

    case BuilderActions.TEMPLATE_EXPORTING:
      return {
        ...state,
        templateExporting: true,
      };

    case BuilderActions.TEMPLATE_EXPORTED:
      return {
        ...state,
        templateExporting: false,
      };

    case BuilderActions.TEMPLATE_EXPORT_ERROR:
      return {
        ...state,
        templateExporting: false,
      };

    case BuilderActions.TEMPLATE_INSTANTIATING:
      return {
        ...state,
        templateInstantiating: true,
      };

    case BuilderActions.TEMPLATE_INSTANTIATED:
      return {
        ...state,
        templateInstantiating: false,
      };

    case BuilderActions.TEMPLATE_INSTANTIATE_ERROR:
      return {
        ...state,
        templateInstantiating: false,
      };

    case BuilderActions.TEMPLATE_CLEAR:
      return {
        ...state,
        currentTemplate: null,
        currentTemplateLoading: false,
      };

    case BuilderActions.DELETING_TEMPLATE:
      return {
        ...state,
        deletingTemplateUid: action.uid,
      };

    case BuilderActions.TEMPLATE_DELETED:
      return {
        ...state,
        deletingTemplateUid: null,
      };

    default:
      return state;
  }
};

export const builderSelectors = {
  // Project list selectors
  getProjectList: (state) => state.builder.projectList,
  getListCounter: (state) => state.builder.listCounter,
  getDeletingProjectUid: (state) => state.builder.deletingProjectUid,

  // Chat selectors
  getChat: (state) => state.builder.chat,
  getMessages: (state) => state.builder.messages,
  getReplyInProgress: (state) => state.builder.replyInProgress,
  getReplyError: (state) => state.builder.replyError,
  getApprovingPlan: (state) => state.builder.approvingPlan,
  getPlanningProgress: (state) => state.builder.planningProgress,

  // Agent selectors
  getAgentSession: (state) => state.builder.agentSession,
  getAgentTodos: (state) => state.builder.agentTodos,
  getAgentCurrentTask: (state) => state.builder.agentCurrentTask,
  getAgentFileChanges: (state) => state.builder.agentFileChanges,
  getAgentToolCalls: (state) => state.builder.agentToolCalls,
  getAgentTokenUsage: (state) => state.builder.agentTokenUsage,
  getAgentError: (state) => state.builder.agentError,
  isAgentRunning: (state) => state.builder.agentSession?.status === "RUNNING",
  isAgentActive: (state) => state.builder.agentSession?.isActive === true,

  // Connection resilience selectors
  getLastEventId: (state) => state.builder.lastEventId,
  shouldResumeAgent: (state) => state.builder.shouldResumeAgent === true,

  // Current project selectors
  getCurrentProject: (state) => state.builder.currentProject,
  isCurrentProjectLoading: (state) => state.builder.currentProjectLoading,
  getCurrentProjectUid: (state) => state.builder.currentProjectUid,

  // Sources (file tree/editor) selectors - use currentProjectUid for isolation
  getSourcesTree: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.tree : null;
  },
  isSourcesTreeLoading: (state) => state.builder.sourcesTreeLoading,
  getSourcesSearchQuery: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.searchQuery || "" : "";
  },
  getSourcesSearchResults: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.searchResults : null;
  },
  isSourcesSearchLoading: (state) => state.builder.sourcesSearchLoading,
  getSourcesOpenTabs: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.openTabs || [] : [];
  },
  getSourcesActiveTabPath: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.activeTabPath : null;
  },
  getSourcesActiveTab: (state) => {
    const projectUid = state.builder.currentProjectUid;
    if (!projectUid) return null;
    const projectState = state.builder.sourcesByProject[projectUid];
    if (!projectState) return null;
    return projectState.openTabs.find((tab) => tab.path === projectState.activeTabPath) || null;
  },
  getSourcesExternalChange: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.externalChange : null;
  },
  getSourcesExpandedPaths: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.expandedPaths || [] : [];
  },
  getSourcesPendingFileChanges: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.pendingFileChanges || [] : [];
  },
  isSourcesReloading: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.isReloading || false : false;
  },
  getSourcesNeedsTreeRefresh: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.sourcesByProject[projectUid]?.needsTreeRefresh || false : false;
  },
  // Get tabs with pending conflicts (dirty + has pendingContent from external change)
  getSourcesConflictTabs: (state) => {
    const projectUid = state.builder.currentProjectUid;
    if (!projectUid) return [];
    const tabs = state.builder.sourcesByProject[projectUid]?.openTabs || [];
    return tabs.filter((tab) => tab.isDirty && tab.pendingContent !== undefined && tab.pendingContent !== null);
  },
  // Get stale tabs (for visual indicator)
  getSourcesStaleTabs: (state) => {
    const projectUid = state.builder.currentProjectUid;
    if (!projectUid) return [];
    const tabs = state.builder.sourcesByProject[projectUid]?.openTabs || [];
    return tabs.filter((tab) => tab.isStale);
  },

  // Project events selectors - use currentProjectUid for isolation
  getProjectEvents: (state) => {
    const projectUid = state.builder.currentProjectUid;
    return projectUid ? state.builder.projectEventsByProject[projectUid] || [] : [];
  },
  isProjectEventsLoading: (state) => state.builder.projectEventsLoading,
  hasActiveProjectEvent: (state) => {
    const projectUid = state.builder.currentProjectUid;
    const events = projectUid ? state.builder.projectEventsByProject[projectUid] || [] : [];
    return events.some((e) => e.status === "PENDING" || e.status === "RUNNING");
  },
  getMostRecentProjectEvent: (state) => {
    const projectUid = state.builder.currentProjectUid;
    const events = projectUid ? state.builder.projectEventsByProject[projectUid] || [] : [];
    return events[0] || null;
  },

  // Template selectors
  getTemplates: (state) => state.builder.templates,
  isTemplatesLoading: (state) => state.builder.templatesLoading,
  getTemplatesError: (state) => state.builder.templatesError,
  getCurrentTemplate: (state) => state.builder.currentTemplate,
  isCurrentTemplateLoading: (state) => state.builder.currentTemplateLoading,
  isTemplateExporting: (state) => state.builder.templateExporting,
  isTemplateInstantiating: (state) => state.builder.templateInstantiating,
  getDeletingTemplateUid: (state) => state.builder.deletingTemplateUid,
};

export default builderReducer;
